import type {
  AssignPaidSlotRequest,
  BillingProduct,
  BillingProductsResponse,
  BillingSubscription,
  BillingSubscriptionStatus,
  FakePurchaseRequest,
  FakePurchaseResponse,
  FakeSubscriptionActionRequest,
  FakeSubscriptionStateResponse,
  CreateInviteRequest,
  CreateRoomRequest,
  CreateSpaceRequest,
  CreatedInvite,
  DecisionResponse,
  InvitesResponse,
  JoinRequestsResponse,
  MembersResponse,
  PaidSpaceSlot,
  PaidSpaceSlotResponse,
  PaidSpaceSlotsResponse,
  PatchReportRequest,
  ReportActionResponse,
  ProfileResponse,
  ReportsResponse,
  ResolveInviteRequest,
  ResolvedInvite,
  Room,
  RoomResponse,
  RoomsResponse,
  SpaceDetailResponse,
  SpaceResponse,
  SpacesResponse
} from "../../shared/api/types";
import { isWebAdminMockMode } from "../../shared/environment";
import {
  MOCK_ADMIN_DATA,
  MOCK_INVITES_BY_SPACE_ID,
  MOCK_JOIN_REQUESTS_BY_SPACE_ID,
  MOCK_PAID_SPACE_SLOTS,
  MOCK_REPORTS,
  MOCK_ROOMS_BY_SPACE_ID,
  MOCK_SPACES,
  type MockReport
} from "./mockData";

const MOCK_NOW = "2026-06-16T09:00:00Z";
const MOCK_PERIOD_END = "2026-07-16T09:00:00Z";

const BILLING_PRODUCTS: readonly BillingProduct[] = [
  {
    product_id: "booply_plus_monthly",
    display_name: "Booply Plus",
    billing_period: "monthly",
    price_usd_cents: 399,
    paid_space_slots: 0
  },
  {
    product_id: "booply_spaces_1_monthly",
    display_name: "Booply Spaces 1",
    billing_period: "monthly",
    price_usd_cents: 1299,
    paid_space_slots: 1
  },
  {
    product_id: "booply_spaces_3_monthly",
    display_name: "Booply Spaces 3",
    billing_period: "monthly",
    price_usd_cents: 3899,
    paid_space_slots: 3
  }
];

let mockPaidSpaceSlots = MOCK_PAID_SPACE_SLOTS.map((slot) => ({ ...slot }));
let mockReports: MockReport[] = MOCK_REPORTS.map((report) => ({ ...report }));
let mockSubscriptions: BillingSubscription[] = [
  subscription("sub_mock_spaces_3", "booply_spaces_3_monthly", "active")
];

export class MockBooplyAdminApi {
  async getProfile(): Promise<ProfileResponse> {
    return clone({ profile: MOCK_ADMIN_DATA.profile });
  }

  async listSpaces(): Promise<SpacesResponse> {
    return clone({ spaces: MOCK_SPACES });
  }

  async getSpace(spaceId: string): Promise<SpaceDetailResponse> {
    const space = findSpace(spaceId);

    return clone({
      space,
      rooms: MOCK_ROOMS_BY_SPACE_ID[spaceId] ?? [],
      members: MOCK_ADMIN_DATA.membersBySpaceId[spaceId] ?? []
    });
  }

  async createSpace(request: CreateSpaceRequest): Promise<SpaceResponse> {
    const space = {
      ...MOCK_SPACES[0],
      space_id: "spc_mock_created",
      name: request.name,
      description: request.description ?? null,
      template_type: request.template_type ?? null,
      join_approval_required: request.join_approval_required ?? true,
      local_discovery_enabled: request.local_discovery_enabled ?? false,
      free_limit_mode: false,
      free_limit_mode_started_at: null
    };

    return clone({ space });
  }

  async listRooms(spaceId: string): Promise<RoomsResponse> {
    findSpace(spaceId);

    return clone({ rooms: MOCK_ROOMS_BY_SPACE_ID[spaceId] ?? [] });
  }

  async createRoom(spaceId: string, request: CreateRoomRequest): Promise<RoomResponse> {
    findSpace(spaceId);

    const room: Room = {
      room_id: "room_mock_created",
      space_id: spaceId,
      name: request.name,
      description: request.description ?? null,
      role_restricted: request.role_restricted ?? false,
      allowed_roles: request.allowed_roles ?? [],
      required_role: null,
      key_epoch: 1,
      created_at: "2026-06-16T09:00:00Z",
      updated_at: "2026-06-16T09:00:00Z"
    };

    return clone({ room });
  }

  async listMembers(spaceId: string): Promise<MembersResponse> {
    findSpace(spaceId);

    return clone({ members: MOCK_ADMIN_DATA.membersBySpaceId[spaceId] ?? [] });
  }

  async listInvites(spaceId: string): Promise<InvitesResponse> {
    findSpace(spaceId);

    return clone({ invites: MOCK_INVITES_BY_SPACE_ID[spaceId] ?? [] });
  }

  async createInvite(spaceId: string, request: CreateInviteRequest): Promise<CreatedInvite> {
    findSpace(spaceId);

    const invite = {
      invite_id: "inv_mock_created",
      space_id: spaceId,
      invite_type: request.invite_type,
      token_hint: request.invite_type === "code" ? "MOCK-****" : `${request.invite_type}-mock-****`,
      use_count: 0,
      expires_at: "2026-06-23T09:00:00Z",
      created_at: "2026-06-16T09:00:00Z",
      revoked_at: null
    };

    return clone({
      invite,
      token: request.invite_type === "code" ? "MOCK-1234" : "mock-token-returned-once"
    });
  }

  async resolveInvite(request: ResolveInviteRequest): Promise<ResolvedInvite> {
    const invite = Object.values(MOCK_INVITES_BY_SPACE_ID)
      .flat()
      .find((candidate) => candidate.invite_type === request.invite_type);

    if (!invite) {
      throw new Error("Mock invite not found.");
    }

    const space = findSpace(invite.space_id);

    return clone({
      invite: {
        invite_id: invite.invite_id,
        space_id: invite.space_id,
        invite_type: invite.invite_type,
        token_hint: invite.token_hint,
        expires_at: invite.expires_at
      },
      space: {
        space_id: space.space_id,
        name: space.name,
        description: space.description,
        join_approval_required: space.join_approval_required
      }
    });
  }

  async listJoinRequests(spaceId: string): Promise<JoinRequestsResponse> {
    findSpace(spaceId);

    return clone({ join_requests: MOCK_JOIN_REQUESTS_BY_SPACE_ID[spaceId] ?? [] });
  }

  async approveJoinRequest(requestId: string): Promise<DecisionResponse> {
    findJoinRequest(requestId);

    return { approved: true };
  }

  async denyJoinRequest(requestId: string): Promise<DecisionResponse> {
    findJoinRequest(requestId);

    return { denied: true };
  }

  async listReports(spaceId: string): Promise<ReportsResponse> {
    findSpace(spaceId);
    const reports = mockReports.filter((report) => report.space_id === spaceId);

    return clone({ reports });
  }

  async patchReport(reportId: string, request: PatchReportRequest): Promise<ReportActionResponse> {
    findReport(reportId);
    mockReports = mockReports.map((report) =>
      report.report_id === reportId
        ? {
          ...report,
          resolved_at: request.status === "resolved" || request.status === "dismissed"
            ? MOCK_NOW
            : report.resolved_at,
          status: request.status ?? report.status
        }
        : report
    );

    return { updated: true };
  }

  async escalateReport(reportId: string): Promise<ReportActionResponse> {
    findReport(reportId);
    mockReports = mockReports.map((report) =>
      report.report_id === reportId
        ? { ...report, status: report.status === "open" ? "in_review" : report.status }
        : report
    );

    return { escalated: true };
  }

  async getBillingProducts(): Promise<BillingProductsResponse> {
    return clone({ products: BILLING_PRODUCTS });
  }

  async getPaidSpaceSlots(): Promise<PaidSpaceSlotsResponse> {
    return clone({ paid_space_slots: mockPaidSpaceSlots });
  }

  async assignPaidSlot(slotId: string, request: AssignPaidSlotRequest): Promise<PaidSpaceSlotResponse> {
    findSpace(request.space_id);
    const slot = findPaidSlot(slotId);
    const assignedToSpace = mockPaidSpaceSlots.some(
      (candidate) => candidate.active && candidate.assigned_space_id === request.space_id
    );

    if (assignedToSpace) {
      throw new Error("This Space already has an assigned paid Space slot.");
    }

    if (!slot.active || slot.assigned_space_id !== null) {
      throw new Error("This paid Space slot is not available.");
    }

    mockPaidSpaceSlots = mockPaidSpaceSlots.map((candidate) =>
      candidate.paid_slot_id === slotId
        ? { ...candidate, assigned_space_id: request.space_id, status: "assigned" }
        : candidate
    );

    return clone({ paid_space_slot: findPaidSlot(slotId) });
  }

  async unassignPaidSlot(slotId: string): Promise<PaidSpaceSlotResponse> {
    const slot = findPaidSlot(slotId);

    mockPaidSpaceSlots = mockPaidSpaceSlots.map((candidate) =>
      candidate.paid_slot_id === slot.paid_slot_id
        ? { ...candidate, assigned_space_id: null, status: "unassigned" }
        : candidate
    );

    return clone({ paid_space_slot: findPaidSlot(slotId) });
  }

  async fakePurchase(request: FakePurchaseRequest): Promise<FakePurchaseResponse> {
    const product = BILLING_PRODUCTS.find((candidate) => candidate.product_id === request.product_id);

    if (!product) {
      throw new Error("Unsupported mock billing product.");
    }

    const subscriptionRecord = subscription(
      `sub_mock_${request.product_id}_${mockSubscriptions.length + 1}`,
      request.product_id,
      "active"
    );

    mockSubscriptions = [subscriptionRecord, ...mockSubscriptions];

    if (product.paid_space_slots > 0) {
      const nextSlotIndex = mockPaidSpaceSlots.length + 1;
      const createdSlots = Array.from({ length: product.paid_space_slots }, (_, index) => ({
        paid_slot_id: `slot_mock_dev_${Date.now()}_${index}`,
        owner_profile_id: MOCK_ADMIN_DATA.profile.profile_id,
        subscription_id: subscriptionRecord.subscription_id,
        slot_index: nextSlotIndex + index,
        assigned_space_id: null,
        active: true,
        status: "unassigned" as const,
        current_period_ends_at: MOCK_PERIOD_END
      }));
      mockPaidSpaceSlots = [...mockPaidSpaceSlots, ...createdSlots];
    }

    return clone({
      subscription: subscriptionRecord,
      paid_space_slots_created: product.paid_space_slots
    });
  }

  async fakeExpire(request: FakeSubscriptionActionRequest): Promise<FakeSubscriptionStateResponse> {
    const subscriptionRecord = findSubscription(request);

    mockSubscriptions = mockSubscriptions.map((candidate) =>
      candidate.subscription_id === subscriptionRecord.subscription_id
        ? { ...candidate, status: "expired", updated_at: MOCK_NOW }
        : candidate
    );

    mockPaidSpaceSlots = mockPaidSpaceSlots.map((slot) =>
      slot.subscription_id === subscriptionRecord.subscription_id
        ? { ...slot, active: false, assigned_space_id: null, status: "canceled" }
        : slot
    );

    return clone({ subscription: findSubscription({ subscription_id: subscriptionRecord.subscription_id }) });
  }
}

export function createMockBooplyAdminApi(): MockBooplyAdminApi {
  return new MockBooplyAdminApi();
}

export const mockBooplyAdminApi = createMockBooplyAdminApi();

export function isAdminMockMode(): boolean {
  return isWebAdminMockMode();
}

function findSpace(spaceId: string) {
  const space = MOCK_SPACES.find((candidate) => candidate.space_id === spaceId);

  if (!space) {
    throw new Error(`Mock Space not found: ${spaceId}`);
  }

  return space;
}

function findReport(reportId: string) {
  const report = mockReports.find((candidate) => candidate.report_id === reportId);

  if (!report) {
    throw new Error(`Mock report not found: ${reportId}`);
  }

  return report;
}

function findJoinRequest(requestId: string) {
  const joinRequest = Object.values(MOCK_JOIN_REQUESTS_BY_SPACE_ID)
    .flat()
    .find((candidate) => candidate.join_request_id === requestId);

  if (!joinRequest) {
    throw new Error(`Mock Join Request not found: ${requestId}`);
  }

  return joinRequest;
}

function findPaidSlot(slotId: string): PaidSpaceSlot {
  const slot = mockPaidSpaceSlots.find((candidate) => candidate.paid_slot_id === slotId);

  if (!slot) {
    throw new Error(`Mock paid Space slot not found: ${slotId}`);
  }

  return slot;
}

function findSubscription(request: FakeSubscriptionActionRequest): BillingSubscription {
  const subscriptionRecord = request.subscription_id
    ? mockSubscriptions.find((candidate) => candidate.subscription_id === request.subscription_id)
    : request.product_id
      ? mockSubscriptions.find((candidate) => candidate.product_id === request.product_id)
      : null;

  if (!subscriptionRecord) {
    throw new Error("Mock subscription not found.");
  }

  return subscriptionRecord;
}

function subscription(
  subscriptionId: string,
  productId: BillingSubscription["product_id"],
  status: BillingSubscriptionStatus
): BillingSubscription {
  return {
    subscription_id: subscriptionId,
    owner_profile_id: MOCK_ADMIN_DATA.profile.profile_id,
    product_id: productId,
    store: "manual",
    store_subscription_ref: `mock:${subscriptionId}`,
    status,
    current_period_started_at: MOCK_NOW,
    current_period_ends_at: MOCK_PERIOD_END,
    created_at: MOCK_NOW,
    updated_at: MOCK_NOW,
    canceled_at: null
  };
}

function clone<T>(value: T): T {
  return JSON.parse(JSON.stringify(value)) as T;
}
